import { useEffect, useRef, useState } from "react";
import { FADE_EFFECT_OBJ, FADE_INTERVEL_OBJ } from "../constants/previewPresent";
import { floatToFloatSec, getHMSToSec } from "../_helpers/utils";

const useAudioPlayer = ({ docPagesMeta, origin, activePageIdx, muted, active = true }) => {
  const audioIntervalId = useRef(null);
  const [docDuration, setDocDuration] = useState(0);
  const [pageStartTimes, setpageStartTimes] = useState([]);
  const [documentTimer, setDocumentTimer] = useState(0);
  const [audioPlayState, setAudioPlayState] = useState("stop");
  const [userToggleMute, setUserToggleMute] = useState(false);

  // ---------- helper: safePlay ----------
  const safePlay = async (media, { preferUnmuted = false } = {}) => {
    if (!media) return Promise.reject(new Error("no-media"));

    try {
      if (preferUnmuted && !muted) {
        try {
          media.muted = false;
          const p = media.play();
          if (p) await p;
          return Promise.resolve();
        } catch (errUnmuted) {
          console.warn("unmuted play rejected, falling back to muted:", errUnmuted);
        }
      }

      // fallback: start muted programmatically (Safari will allow muted autoplay)
      const wasMuted = media.muted;
      media.muted = true;
      try {
        const p2 = media.play();
        if (p2) await p2;
      } catch (errMuted) {
        console.warn("muted play failed:", errMuted);
        media.muted = wasMuted;
        return Promise.reject(errMuted);
      }
      return Promise.resolve();
    } catch (err) {
      console.warn("safePlay unexpected error:", err);
      return Promise.reject(err);
    }
  };

  // ---------- audio helper object ----------
  const audioPlayHelper = {
    getAudioData: audioObj => {
      const audio = document.getElementById(`player-${audioObj.id}`) || null;
      const safeCurTimeMs = audio ? floatToFloatSec(audio.currentTime) * 1000 : 0;

      const durationInHMS = audioObj.data["data-duration"];
      const durationInSec = getHMSToSec({ hms: durationInHMS });

      const fadeInValue = audioObj.data["data-fade-in-value"];
      const fadeOutValue = audioObj.data["data-fade-out-value"];
      const fadeInMs = parseInt(Object.keys(FADE_EFFECT_OBJ).find(k => FADE_EFFECT_OBJ[k].includes(fadeInValue))) || 0;
      const fadeOutMs =
        parseInt(Object.keys(FADE_EFFECT_OBJ).find(k => FADE_EFFECT_OBJ[k].includes(fadeOutValue))) || 0;

      const trim = audioObj.data["data-trim"];
      const trimArray = trim ? trim.split(",")?.map(e => parseFloat(e)) : null;
      const isTrimmed = Boolean(trimArray?.length === 2);
      const trimStart = isTrimmed ? trimArray?.[0] : 0;
      const trimStop = isTrimmed ? trimArray?.[1] : durationInSec;

      const volume = parseInt(audioObj.data["data-volume"] ?? 100);
      const startTimeMs = parseFloat(audioObj.startTime) * 1000;
      const stopTimeMs = parseFloat(audioObj.endTime) * 1000;
      const trimStartTimeMs = trimStart * 1000;
      const trimStopTimeMs = trimStop * 1000;
      const trimmedDurationMs = trimStopTimeMs - trimStartTimeMs;

      const fadeInStartTimeMs = startTimeMs;
      const fadeInStopTimeMs = startTimeMs + fadeInMs;
      const fadeOutStartTimeMs = stopTimeMs - fadeOutMs;
      const fadeOutStopTimeMs = stopTimeMs;

      let fadeData = {};
      if (fadeInMs > 0 || fadeOutMs > 0) {
        fadeData = audioPlayHelper.getFadeData({ volume, fadeInStartTimeMs, fadeInMs, fadeOutStartTimeMs, fadeOutMs });
      }

      return {
        audio,
        audioCurTimeMs: safeCurTimeMs,
        volume,
        startTimeMs,
        stopTimeMs,
        trimStartTimeMs,
        trimStopTimeMs,
        trimmedDurationMs,
        fadeInStartTimeMs,
        fadeInStopTimeMs,
        fadeOutStartTimeMs,
        fadeOutStopTimeMs,
        ...fadeData,
        isMuted: Boolean(volume === 0),
        isLooped: Boolean(stopTimeMs > trimStopTimeMs),
        isTrimmed: Boolean(trimStartTimeMs > 0 || trimStopTimeMs < stopTimeMs),
        isFadeIn: Boolean(fadeInMs > 0),
        isFadeOut: Boolean(fadeOutMs > 0),
      };
    },

    getFadeData: ({ volume, fadeInStartTimeMs, fadeInMs, fadeOutStartTimeMs, fadeOutMs }) => {
      let fadeInIntervals = [];
      let fadeInVolumes = [];
      let fadeOutIntervals = [];
      let fadeOutVolumes = [];

      if (fadeInMs > 0) {
        const intervals = FADE_INTERVEL_OBJ[fadeInMs] || [];
        const volumeFactor = volume / Math.max(1, intervals.length - 1);

        for (let i = 0; i < intervals.length; i++) {
          fadeInIntervals.push(fadeInStartTimeMs + intervals[i]);
          if (i === 0) fadeInVolumes.push(0);
          else if (i === intervals.length - 1) fadeInVolumes.push(volume);
          else fadeInVolumes.push(parseInt(i * volumeFactor));
        }
      }

      if (fadeOutMs > 0) {
        const intervalObj = JSON.parse(JSON.stringify(FADE_INTERVEL_OBJ[fadeOutMs] || []));
        const intervals = intervalObj.reverse();
        const volumeFactor = volume / Math.max(1, intervals.length - 1);

        for (let i = intervals.length - 1; i >= 0; i--) {
          fadeOutIntervals.push(fadeOutStartTimeMs + intervals[i]);
          if (i === intervals.length - 1) fadeOutVolumes.push(volume);
          else if (i === 0) fadeOutVolumes.push(0);
          else fadeOutVolumes.push(parseInt(i * volumeFactor));
        }
      }

      return { fadeInIntervals, fadeInVolumes, fadeOutIntervals, fadeOutVolumes };
    },

    getPageStartTimes: () => {
      const pageStartTimes = [];
      let documentDurationMs = 0;
      (docPagesMeta?.pageNodes || []).forEach(pageObj => {
        const pageDurationMs = parseFloat(pageObj.pageDuration) * 1000;
        pageStartTimes.push(documentDurationMs);
        documentDurationMs += pageDurationMs;
      });
      return pageStartTimes;
    },

    getactvePageStartTime: ({ pageIdx }) => {
      let timeNow = 0;
      (docPagesMeta?.pageNodes || []).forEach(pn => {
        if ((pn.pageIdx ?? pn.pageIdx) < pageIdx) {
          timeNow += parseFloat(pn.pageDuration);
        }
      });
      return timeNow * 1000;
    },

    getDocumentDurationMS: () => {
      let totalDuartion = 0;
      (docPagesMeta?.pageNodes || []).forEach(pn => {
        totalDuartion += parseFloat(pn.pageDuration || 0);
      });
      return totalDuartion * 1000;
    },

    setAudioCurTime: ({ timer }) => {
      (docPagesMeta?.audios || []).forEach(audioObj => {
        const { audio, startTimeMs, stopTimeMs, trimStartTimeMs, trimStopTimeMs, volume, isMuted } =
          audioPlayHelper.getAudioData(audioObj);

        if (!audio) return;

        audio.volume = (volume ?? 100) / 100;
        audio.muted = Boolean(muted) || Boolean(isMuted);

        if (timer > startTimeMs && timer < stopTimeMs) {
          const widgetPlayedDurationMs = timer - startTimeMs;
          const audioTrimmedDurationMs = trimStopTimeMs - trimStartTimeMs;
          const audioCurTimeWithinPlayableRange =
            (audioTrimmedDurationMs > widgetPlayedDurationMs
              ? widgetPlayedDurationMs
              : widgetPlayedDurationMs % audioTrimmedDurationMs) / 1000;
          audio.currentTime = trimStartTimeMs / 1000 + audioCurTimeWithinPlayableRange;
        } else {
          audio.currentTime = trimStartTimeMs / 1000;
        }
        audio.pause();
      });
    },

    audioPlay: ({ timer }) => {
      const currentPageIdx = Math.max(0, (activePageIdx || 1) - 1);
      const pageStart = audioPlayHelper.getactvePageStartTime({ pageIdx: currentPageIdx });
      const pageDuration = parseFloat(docPagesMeta?.pageNodes?.[currentPageIdx]?.pageDuration || 0) * 1000;
      const pageEnd = pageStart + pageDuration;

      (docPagesMeta?.audios || []).forEach(async audioObj => {
        const {
          audio,
          audioCurTimeMs,
          volume,
          startTimeMs,
          stopTimeMs,
          trimStartTimeMs,
          trimStopTimeMs,
          fadeInIntervals,
          fadeInVolumes,
          fadeOutIntervals,
          fadeOutVolumes,
          isMuted,
          isLooped,
          isFadeIn,
          isFadeOut,
        } = audioPlayHelper.getAudioData(audioObj);

        if (!audio) return;

        if (origin === "present" && startTimeMs >= pageEnd) {
          if (!audio.paused) {
            audio.pause();
            audio.removeAttribute("data-playing");
            audio.currentTime = trimStartTimeMs / 1000;
          }
          return;
        }

        // Use ranges instead of strict equality (interval granularity)
        const nearEqual = (a, b, tol = 25) => Math.abs(a - b) <= tol;

        if (nearEqual(timer, startTimeMs)) {
          audio.currentTime = trimStartTimeMs / 1000;
          if (isMuted) audio.muted = true;
          const preferUnmuted = !muted && !isMuted;

          try {
            await safePlay(audio, { preferUnmuted });
            audio.setAttribute("data-playing", "true");
          } catch (err) {
            console.warn("audio start failed for", audioObj.id, err);
          }
        }

        // fade in/out handling
        if (isFadeIn && fadeInIntervals.includes(timer)) {
          const idx = fadeInIntervals.indexOf(timer);
          audio.volume = (fadeInVolumes[idx] ?? 0) / 100;
        }
        if (isFadeOut && fadeOutIntervals.includes(timer)) {
          const idx = fadeOutIntervals.indexOf(timer);
          audio.volume = (fadeOutVolumes[idx] ?? 0) / 100;
        }

        if (timer > startTimeMs && timer < stopTimeMs) {
          // If current time not in trimmed range, reset it
          if (!(audioCurTimeMs >= trimStartTimeMs && audioCurTimeMs <= trimStopTimeMs)) {
            audio.currentTime = trimStartTimeMs / 1000;
            if (isLooped) {
              if (audio.paused) {
                safePlay(audio, { preferUnmuted: !muted }).catch(() => {});
                audio.setAttribute("data-playing", "true");
              }
            } else {
              audio.pause();
            }
          } else {
            if (audio.paused) {
              safePlay(audio, { preferUnmuted: !muted }).catch(() => {});
              audio.setAttribute("data-playing", "true");
            }
          }
        }

        if (nearEqual(timer, stopTimeMs)) {
          audio.pause();
          if (isMuted) audio.muted = true;
          audio.removeAttribute("data-playing");
          audio.currentTime = trimStartTimeMs / 1000;
        }
      });
    },

    stopAudioPlay: () => {
      if (audioIntervalId.current) {
        clearInterval(audioIntervalId.current);
        audioIntervalId.current = null;
      }
      document.querySelectorAll("audio").forEach(audioNode => {
        try {
          audioNode.pause();
          audioNode.removeAttribute("data-playing");
        } catch (e) {
          // ignore
        }
      });
    },
  };

  // ---------- init durations when docPagesMeta changes ----------
  useEffect(() => {
    if (docPagesMeta?.pageNodes?.length) {
      setDocDuration(audioPlayHelper.getDocumentDurationMS());
      setpageStartTimes(audioPlayHelper.getPageStartTimes());
    }
  }, [docPagesMeta]);

  // ---------- react to global muted prop ----------
  useEffect(() => {
    if (!active) return;
    (docPagesMeta?.audios || []).forEach(aObj => {
      const el = document.getElementById(`player-${aObj.id}`);
      if (el) el.muted = Boolean(muted);
    });

    if (!userToggleMute && muted === false) {
      setUserToggleMute(true);
      setAudioPlayState("play");
    }
  }, [muted, docPagesMeta, active]);

  // ---------- start/stop interval handler ----------
  useEffect(() => {
    if (!active) return;
    if (audioPlayState === "stop") {
      audioPlayHelper.stopAudioPlay();
      return;
    }

    if (audioPlayState === "play") {
      if (audioIntervalId.current) {
        clearInterval(audioIntervalId.current);
        audioIntervalId.current = null;
      }

      let timer = activePageIdx ? audioPlayHelper.getactvePageStartTime({ pageIdx: activePageIdx - 1 }) : 0;

      if (timer === 0) {
        audioPlayHelper.audioPlay({ timer });
      }

      audioIntervalId.current = setInterval(() => {
        timer += 20;
        audioPlayHelper.audioPlay({ timer });
        setDocumentTimer(timer);
      }, 20);
    }

    return () => {
      if (audioIntervalId.current) {
        clearInterval(audioIntervalId.current);
        audioIntervalId.current = null;
      }
    };
  }, [audioPlayState, docPagesMeta, muted]);

  // ---------- stop conditions based on timer ----------
  useEffect(() => {
    if (!docDuration || !active) return;
    if (
      (documentTimer >= docDuration && origin === "preview") ||
      (origin === "present" &&
        pageStartTimes &&
        (documentTimer >= pageStartTimes[activePageIdx] || documentTimer >= docDuration))
    ) {
      setAudioPlayState("stop");
    }
  }, [documentTimer, docDuration, pageStartTimes, origin, activePageIdx]);

  // ---------- respond to page changes (present mode) ----------
  useEffect(() => {
    if (!(docPagesMeta?.audios && origin === "present") || !active) return;

    const newStartTime = audioPlayHelper.getactvePageStartTime({ pageIdx: Math.max(0, (activePageIdx || 1) - 1) });

    audioPlayHelper.stopAudioPlay();
    setAudioPlayState("stop");
    setDocumentTimer(newStartTime);

    if (activePageIdx === 1) {
      setDocDuration(audioPlayHelper.getDocumentDurationMS());
      setpageStartTimes(audioPlayHelper.getPageStartTimes());
    }

    audioPlayHelper.setAudioCurTime({ timer: newStartTime });

    setUserToggleMute(false);

    requestAnimationFrame(() => setAudioPlayState("play"));
  }, [docPagesMeta?.audios, activePageIdx, origin]);

  // ---------- cleanup on unmount ----------
  useEffect(() => {
    if (active)
      return () => {
        audioPlayHelper.stopAudioPlay();
      };
  }, []);

  return null;
};

export default useAudioPlayer;
